import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { createPersona, updatePersona } from "../api/VejezAPI";
import { useAuth } from "../../../inicio/context/AuthContext";
import { useOnlineStatus } from "../../../inicio/hooks/useSync";
import { guardarOfflineVejez } from "../offline/vejezOffline";
import { manejarErrorDuplicado } from "../utils/handleDuplicado";
import { obtenerTerritorios, obtenerAatis, obtenerComunidades, obtenerEapbs, obtenerEtnias } from "../../../inicio/offline/listasCache";

const TOTAL_PASOS = 8;

// Esta es la estrategia Vejez — "Persona mayor" siempre debe quedar
// marcada acá, sin poder destildarla (por eso se está caracterizando bajo
// esta estrategia). Cuando se construyan Migrante/Habitante de Calle,
// cada una bloqueará la suya de la misma forma.
const ESTRATEGIA_ACTUAL = "Persona mayor";

const TIPOS_DOC = ["R.C","T.I","C.C","C.E","Pasaporte","Otro"];
const GRUPO_POBLACIONAL_OPCIONES = [
  "Persona mayor","Migrante","Habitante de calle","Víctima del conflicto armado",
  "Población indígena","Afrodescendiente","Campesino/a","Persona con discapacidad","LGBTIQ+","Otro",
];
const BARRERAS_OPCIONES = ["Geográficas","Económicas","Culturales","Comunicativas","Administrativas","Discriminación","Transporte","Documentación","Otra"];
const PERTENENCIA_OPCIONES = ["Indígena","Afrodescendiente","Negra","Raizal","Palenquera","Rrom","Campesina","Otra"];
const SERVICIOS_BASICOS_OPCIONES = ["Agua","Energía","Alcantarillado","Internet","Otro"];
const RED_APOYO_OPCIONES = ["Familiar","Comunitaria","Institucional","Ninguna"];
const VIOLENCIAS_OPCIONES = ["Física","Psicológica/emocional","Sexual","Económica/patrimonial","Verbal","Intrafamiliar"];
const OTRAS_VULNERACIONES_OPCIONES = ["Negligencia","Abandono","Maltrato","Discriminación","Explotación","Despojo/apropiación de bienes","Aislamiento/restricción de contacto","Otra"];
const NECESIDADES_OPCIONES = ["Alimentación","Salud","Vivienda","Ingresos","Empleo","Documentación","Apoyo psicológico","Apoyo familiar","Apoyo técnico","Protección","Acceso a justicia","Restablecimiento de derechos","Otra"];
const RUTA_ATENCION_OPCIONES = ["Medicina","Psicología","Trabajo Social","Enfermería","Terapia Ocupacional","Protección/Restablecimiento de Derechos","Orientación jurídica","Otra"];

const parseFecha = (f) => {
  if (!f) return { dia:"", mes:"", anio:"" };
  const [anio, mes, dia] = f.split("T")[0].split("-");
  return { dia: parseInt(dia,10).toString(), mes: parseInt(mes,10).toString(), anio };
};
const calcularEdad = (dia, mes, anio) => {
  if (!dia || !mes || !anio) return 0;
  const nac = new Date(parseInt(anio), parseInt(mes)-1, parseInt(dia));
  const hoy = new Date();
  let anios = hoy.getFullYear() - nac.getFullYear();
  if (hoy.getMonth() < nac.getMonth() || (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())) anios--;
  return Math.max(0, anios);
};

const initialForm = {
  fecha_dia:"", fecha_mes:"", fecha_anio:"",
  municipio:"", barrio:"", nombre_comunidad:"",
  primer_nombre:"", segundo_nombre:"", primer_apellido:"", segundo_apellido:"",
  tipo_documento:"", documento:"",
  fecha_nac_dia:"", fecha_nac_mes:"", fecha_nac_anio:"", edad_anios:"0",
  genero:"", estado_civil:"", telefono:"", direccion:"",
  eps:"", regimen_eps:"", nivel_educativo:"", ocupacion:"",
  latitud:"", longitud:"", precision_gps:"",

  grupo_poblacional: [], tipo_discapacidad:"", grupo_poblacional_otro:"",
  reconoce_grupo_diferencial:"", pertenencia_diferencial: [], etnia:"",
  tiene_condicion_diferencial:"", condicion_diferencial_cual:"",
  barreras_identificadas: [],

  pais_origen:"", situacion_migratoria:"", documentos_vigentes:"",
  fecha_ingreso_colombia:"", motivo_migracion:"", red_apoyo_familiar:"", dificultad_salud_migratoria:"",

  enfermedades_cronicas:"", enfermedades_cronicas_cual:"", vacunacion_al_dia:"",
  controles_medicos:"", observaciones_salud:"", estado_funcional:"",
  requiere_apoyo_tecnico:"", apoyo_tecnico_cual:"", estado_animo:"", animo_interfiere_actividades:"",

  tipo_vivienda:"", servicios_basicos: [], ingresos:"", ingresos_suficientes:"", red_apoyo: [],

  victima_violencia:"", violencias_identificadas: [], otras_vulneraciones: [],
  otras_vulneraciones_cual:"", situacion_activa:"", presunto_responsable:"", descripcion_observaciones:"",

  necesidades_principales: [], necesidades_otra:"",
  requiere_activacion: [], requiere_activacion_otra:"",

  funcionario1_nombre:"", funcionario1_documento:"", funcionario1_cargo:"",
  funcionario2_nombre:"", funcionario2_documento:"", funcionario2_cargo:"",
  funcionario3_nombre:"", funcionario3_documento:"", funcionario3_cargo:"",

  gestor:"",
};

export default function VejezModal({ show, onClose, reload, persona }) {
  const { user } = useAuth();
  const online = useOnlineStatus();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errores, setErrores] = useState({});
  const [loading, setLoading] = useState(false);
  const [obteniendoGps, setObteniendoGps] = useState(false);
  const [errorGps, setErrorGps] = useState("");

  const [territorios, setTerritorios] = useState([]);
  const [aatisTodas, setAatisTodas] = useState([]);
  const [comunidadesTodas, setComunidadesTodas] = useState([]);
  const [eapbs, setEapbs] = useState([]);
  const [etniasDb, setEtniasDb] = useState([]);

  useEffect(() => {
    if (!show) return;
    obtenerTerritorios().then(setTerritorios).catch(() => setTerritorios([]));
    obtenerAatis().then(setAatisTodas).catch(() => setAatisTodas([]));
    obtenerComunidades().then(setComunidadesTodas).catch(() => setComunidadesTodas([]));
    obtenerEapbs().then(setEapbs).catch(() => setEapbs([]));
    obtenerEtnias().then(setEtniasDb).catch(() => setEtniasDb([]));
  }, [show]);

  const comunidades = form.municipio
    ? comunidadesTodas.filter(c => (c.territorio||"").trim().toLowerCase() === form.municipio.trim().toLowerCase())
    : [];
  const opcionesTerritorios = territorios.map(t => ({ value: t.nombre.trim(), label: t.nombre.trim() }));
  const opcionesComunidades = comunidades.map(c => ({ value: c.comunidad, label: c.microterritorio ? `${c.comunidad} (${c.microterritorio})` : c.comunidad }));
  const opcionesEapbs = [{ value: "NO ESTÁ AFILIADO", label: "NO ESTÁ AFILIADO" }, ...eapbs.map(e => ({ value: e.nombre, label: e.nombre }))];
  const opcionesEtnias = etniasDb.map(e => ({ value: e.nombre, label: e.nombre }));
  void aatisTodas; // reservado para cuando se necesite AATI acá también

  const estiloSelectInvalido = (inv) => ({ control: (b) => ({ ...b, borderColor: inv ? "#dc3545" : b.borderColor }) });

  useEffect(() => {
    if (!show) { resetFields(); return; }
    if (persona) {
      const fApl = parseFecha(persona.fecha);
      const fNac = parseFecha(persona.fecha_nacimiento);
      setForm({
        ...initialForm,
        ...Object.fromEntries(Object.entries(persona).map(([k,v]) => [
          k, Array.isArray(initialForm[k]) ? (Array.isArray(v) ? v : []) : (v ?? "")
        ])),
        fecha_dia:fApl.dia, fecha_mes:fApl.mes, fecha_anio:fApl.anio,
        fecha_nac_dia:fNac.dia, fecha_nac_mes:fNac.mes, fecha_nac_anio:fNac.anio,
        grupo_poblacional: Array.from(new Set([...(persona.grupo_poblacional || []), ESTRATEGIA_ACTUAL])),
      });
      setStep(1); return;
    }
    resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, persona]);

  useEffect(() => {
    if (!show || persona) return;
    setForm(prev => ({ ...prev, gestor: user?.nombre || prev.gestor || "" }));
  }, [show, user, persona]);

  useEffect(() => {
    if (form.fecha_nac_dia && form.fecha_nac_mes && form.fecha_nac_anio) {
      setForm(prev => ({ ...prev, edad_anios: String(calcularEdad(form.fecha_nac_dia, form.fecha_nac_mes, form.fecha_nac_anio)) }));
    }
  }, [form.fecha_nac_dia, form.fecha_nac_mes, form.fecha_nac_anio]);

  // Georreferenciación obligatoria — se pide sola al llegar al paso 1 (donde vive el resto de campos de ubicación).
  useEffect(() => {
    if (show && step === 1 && !form.latitud && !form.longitud) obtenerUbicacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, step]);

  const resetFields = () => {
    const hoy = new Date();
    setForm({
      ...initialForm,
      gestor: user?.nombre || "",
      fecha_dia: String(hoy.getDate()),
      fecha_mes: String(hoy.getMonth() + 1),
      fecha_anio: String(hoy.getFullYear()),
      grupo_poblacional: [ESTRATEGIA_ACTUAL],
    });
    setStep(1); setErrores({});
  };
  const f = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const req = txt => <>{txt} <span style={{ color:"red" }}>*</span></>;

  const toggleMulti = (campo, valor) => {
    if (campo === "grupo_poblacional" && valor === ESTRATEGIA_ACTUAL) return; // bloqueado, no se puede destildar
    const arr = form[campo] || [];
    f(campo, arr.includes(valor) ? arr.filter(x => x !== valor) : [...arr, valor]);
  };

  const obtenerUbicacion = () => {
    setErrorGps("");
    if (!navigator.geolocation) { setErrorGps("Este dispositivo/navegador no soporta geolocalización."); return; }
    setObteniendoGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        f("latitud", pos.coords.latitude.toFixed(6));
        f("longitud", pos.coords.longitude.toFixed(6));
        f("precision_gps", Math.round(pos.coords.accuracy));
        setObteniendoGps(false);
      },
      (err) => {
        setObteniendoGps(false);
        const mensajes = { 1:"Permiso de ubicación denegado.", 2:"No se pudo determinar la ubicación.", 3:"Se agotó el tiempo esperando la ubicación." };
        setErrorGps(mensajes[err.code] || "No se pudo obtener la ubicación.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const esMigrante = form.grupo_poblacional.includes("Migrante");
  const esDiscapacidad = form.grupo_poblacional.includes("Persona con discapacidad");

  const validar = () => {
    const e = {};
    if (step === 1) {
      if (!form.municipio) e.municipio = "Obligatorio";
      if (!form.nombre_comunidad) e.nombre_comunidad = "Obligatorio";
      if (!form.primer_nombre) e.primer_nombre = "Obligatorio";
      if (!form.primer_apellido) e.primer_apellido = "Obligatorio";
      if (!form.documento) e.documento = "Obligatorio";
      if (!form.tipo_documento) e.tipo_documento = "Obligatorio";
      if (!form.fecha_nac_dia || !form.fecha_nac_mes || !form.fecha_nac_anio) e.fecha_nac = "Obligatoria";
      if (!form.genero) e.genero = "Obligatorio";
      if (!form.direccion) e.direccion = "Obligatorio";
      if (!form.latitud || !form.longitud) e.ubicacion = "Obligatorio — captura la ubicación GPS o escríbela a mano";
    }
    if (step === 2) {
      if (!form.grupo_poblacional.length) e.grupo_poblacional = "Selecciona al menos una opción";
      if (esDiscapacidad && !form.tipo_discapacidad) e.tipo_discapacidad = "Obligatorio si marcó 'Persona con discapacidad'";
      if (!form.reconoce_grupo_diferencial) e.reconoce_grupo_diferencial = "Obligatorio";
      if (form.reconoce_grupo_diferencial === "Si" && !form.pertenencia_diferencial.length) e.pertenencia_diferencial = "Obligatorio — selecciona al menos una";
      if (form.pertenencia_diferencial.includes("Indígena") && !form.etnia) e.etnia = "Obligatorio si marcó 'Indígena'";
    }
    return e;
  };

  const nextStep = () => {
    const e = validar();
    if (Object.keys(e).length) { setErrores(e); return; }
    setErrores({});
    // Salta el paso 3 (Condición migratoria) si no marcó "Migrante".
    let siguiente = step + 1;
    if (siguiente === 3 && !esMigrante) siguiente = 4;
    if (siguiente <= TOTAL_PASOS) setStep(siguiente);
  };
  const prevStep = () => {
    let anterior = step - 1;
    if (anterior === 3 && !esMigrante) anterior = 2;
    if (anterior >= 1) setStep(anterior);
  };

  const handleClose = async () => {
    const r = await Swal.fire({ title:"¿Salir del formulario?", text:"El progreso no guardado se perderá.", icon:"warning", showCancelButton:true, confirmButtonColor:"#d33", cancelButtonColor:"#3085d6", confirmButtonText:"Sí, salir", cancelButtonText:"Cancelar" });
    if (r.isConfirmed) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validar();
    if (Object.keys(errs).length) { setErrores(errs); return; }
    try {
      setLoading(true);
      const payload = {
        ...form,
        fecha: form.fecha_anio && form.fecha_mes && form.fecha_dia ? `${form.fecha_anio}-${String(form.fecha_mes).padStart(2,"0")}-${String(form.fecha_dia).padStart(2,"0")}` : "",
        fecha_nacimiento: form.fecha_nac_anio && form.fecha_nac_mes && form.fecha_nac_dia ? `${form.fecha_nac_anio}-${String(form.fecha_nac_mes).padStart(2,"0")}-${String(form.fecha_nac_dia).padStart(2,"0")}` : "",
        gestor: user?.nombre || form.gestor,
      };
      const estaOnline = online && navigator.onLine;
      if (estaOnline) {
        if (persona) {
          await updatePersona(persona.id, payload);
          await Swal.fire({ icon:"success", title:"✏️ Registro actualizado", timer:1500, showConfirmButton:false });
        } else {
          await createPersona(payload);
          await Swal.fire({ icon:"success", title:"✅ Persona registrada", timer:1500, showConfirmButton:false });
        }
      } else {
        await guardarOfflineVejez(payload, persona?.id);
        await Swal.fire({ icon:"info", title:"📴 Sin conexión", text:"El registro se guardó localmente y se sincronizará al reconectarse.", confirmButtonColor:"#6f42c1" });
      }
      onClose();
      if (reload) await reload();
    } catch (error) {
      if (!manejarErrorDuplicado(error, "Vejez")) {
        Swal.fire({ icon:"error", title:"Error al guardar", text: error.response?.data?.message || error.message, confirmButtonColor:"#d33" });
      }
    } finally { setLoading(false); }
  };

  const CheckGroup = ({ campo, opciones, label, errorKey, bloqueados = [] }) => (
    <Form.Group className="mb-3">
      {label && <Form.Label className="d-block fw-semibold">{label}</Form.Label>}
      <div className="d-flex flex-wrap gap-3">
        {opciones.map(op => {
          const bloqueada = bloqueados.includes(op);
          return (
            <Form.Check key={op} type="checkbox" label={bloqueada ? `${op} 🔒` : op}
              checked={(form[campo]||[]).includes(op)} disabled={bloqueada}
              onChange={() => toggleMulti(campo, op)} />
          );
        })}
      </div>
      {errorKey && errores[errorKey] && <div className="text-danger mt-1" style={{fontSize:"0.875em"}}>{errores[errorKey]}</div>}
    </Form.Group>
  );
  const SiNo = ({ campo, label }) => (
    <Form.Group className="mb-3">
      {label && <Form.Label className="d-block">{label}</Form.Label>}
      <div className="d-flex gap-3">
        {["Si","No"].map(op => <Form.Check key={op} type="radio" inline label={op} checked={form[campo]===op} onChange={()=>f(campo,op)} />)}
      </div>
      {errores[campo] && <div className="text-danger" style={{fontSize:"0.875em"}}>{errores[campo]}</div>}
    </Form.Group>
  );

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Caracterización — Vejez
          {!online && <span className="badge bg-warning text-dark ms-2" style={{fontSize:"0.7rem"}}>📴 Offline</span>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-4">
          <div style={{ height:8, background:"#ddd", borderRadius:20 }}>
            <div style={{ width:`${(step/TOTAL_PASOS)*100}%`, height:8, background:"#0d6efd", borderRadius:20, transition:"width .3s" }} />
          </div>
          <p className="mt-2 text-muted small">Paso {step} de {TOTAL_PASOS}</p>
        </div>

        <Form onSubmit={handleSubmit}>

          {step === 1 && (
            <>
              <h5 className="mb-3">1. Información general y ubicación</h5>
              <Form.Group className="mb-3">
                <Form.Label>Fecha de aplicación</Form.Label>
                <Form.Control
                  value={form.fecha_dia && form.fecha_mes && form.fecha_anio ? `${form.fecha_dia.padStart(2,"0")}/${form.fecha_mes.padStart(2,"0")}/${form.fecha_anio}` : ""}
                  readOnly plaintext style={{ background:"#f8f9fa", fontWeight:500 }}
                />
              </Form.Group>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <Form.Label>{req("Municipio")}</Form.Label>
                  <Select classNamePrefix="rs" styles={estiloSelectInvalido(!!errores.municipio)} placeholder="Buscar municipio..."
                    options={opcionesTerritorios} value={opcionesTerritorios.find(o=>o.value===form.municipio)||null}
                    onChange={opt=>{ f("municipio", opt?.value||""); f("nombre_comunidad",""); }} />
                  {errores.municipio && <div className="text-danger mt-1" style={{fontSize:"0.875em"}}>{errores.municipio}</div>}
                </div>
                <div className="col-md-6">
                  <Form.Label>{req("Comunidad")}</Form.Label>
                  <Select classNamePrefix="rs" styles={estiloSelectInvalido(!!errores.nombre_comunidad)}
                    placeholder={form.municipio ? "Buscar comunidad..." : "Selecciona primero el municipio"}
                    isDisabled={!form.municipio} options={opcionesComunidades}
                    value={opcionesComunidades.find(o=>o.value===form.nombre_comunidad)||null}
                    onChange={opt=>f("nombre_comunidad", opt?.value||"")} />
                  {errores.nombre_comunidad && <div className="text-danger mt-1" style={{fontSize:"0.875em"}}>{errores.nombre_comunidad}</div>}
                </div>
              </div>

              <Form.Group className="mb-3"><Form.Label>Barrio</Form.Label><Form.Control value={form.barrio} onChange={e=>f("barrio",e.target.value)} /></Form.Group>

              <div className="row g-3 mb-3">
                <div className="col-md-6"><Form.Label>{req("Primer nombre")}</Form.Label><Form.Control value={form.primer_nombre} isInvalid={!!errores.primer_nombre} onChange={e=>f("primer_nombre",e.target.value)} /></div>
                <div className="col-md-6"><Form.Label>Segundo nombre</Form.Label><Form.Control value={form.segundo_nombre} onChange={e=>f("segundo_nombre",e.target.value)} /></div>
                <div className="col-md-6"><Form.Label>{req("Primer apellido")}</Form.Label><Form.Control value={form.primer_apellido} isInvalid={!!errores.primer_apellido} onChange={e=>f("primer_apellido",e.target.value)} /></div>
                <div className="col-md-6"><Form.Label>Segundo apellido</Form.Label><Form.Control value={form.segundo_apellido} onChange={e=>f("segundo_apellido",e.target.value)} /></div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <Form.Label>{req("Tipo de documento")}</Form.Label>
                  <Form.Select value={form.tipo_documento} isInvalid={!!errores.tipo_documento} onChange={e=>f("tipo_documento",e.target.value)}>
                    <option value="">Seleccione</option>
                    {TIPOS_DOC.map(t=><option key={t}>{t}</option>)}
                  </Form.Select>
                </div>
                <div className="col-md-6"><Form.Label>{req("Número de documento")}</Form.Label><Form.Control value={form.documento} isInvalid={!!errores.documento} onChange={e=>f("documento",e.target.value)} /></div>
              </div>

              <Form.Label>{req("Fecha de nacimiento")}</Form.Label>
              {errores.fecha_nac && <div className="text-danger mb-2" style={{fontSize:"0.875em"}}>{errores.fecha_nac}</div>}
              <div className="d-flex gap-2 mb-1">
                <Form.Control placeholder="Día" type="number" min="1" max="31" value={form.fecha_nac_dia} onChange={e=>f("fecha_nac_dia",e.target.value)} style={{width:80}} />
                <Form.Control placeholder="Mes" type="number" min="1" max="12" value={form.fecha_nac_mes} onChange={e=>f("fecha_nac_mes",e.target.value)} style={{width:80}} />
                <Form.Control placeholder="Año" type="number" value={form.fecha_nac_anio} onChange={e=>f("fecha_nac_anio",e.target.value)} style={{width:110}} />
              </div>
              {form.fecha_nac_anio && <div style={{fontSize:12,color:"#059669",marginBottom:12}}>✅ Edad calculada: <strong>{form.edad_anios} años</strong></div>}

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <Form.Label>{req("Género")}</Form.Label>
                  <Form.Select value={form.genero} isInvalid={!!errores.genero} onChange={e=>f("genero",e.target.value)}>
                    <option value="">Seleccione</option>
                    <option>Masculino</option><option>Femenino</option><option>Otro</option>
                  </Form.Select>
                </div>
                <div className="col-md-6">
                  <Form.Label>Estado civil</Form.Label>
                  <Form.Select value={form.estado_civil} onChange={e=>f("estado_civil",e.target.value)}>
                    <option value="">Seleccione</option>
                    <option>Soltero(a)</option><option>Casado(a)</option><option>Unión libre</option><option>Separado(a)</option><option>Viudo(a)</option>
                  </Form.Select>
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6"><Form.Label>Teléfono</Form.Label><Form.Control value={form.telefono} onChange={e=>f("telefono",e.target.value)} /></div>
                <div className="col-md-6"><Form.Label>{req("Dirección")}</Form.Label><Form.Control value={form.direccion} isInvalid={!!errores.direccion} onChange={e=>f("direccion",e.target.value)} /></div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <Form.Label>EPS</Form.Label>
                  <Select classNamePrefix="rs" placeholder="Buscar EPS..." options={opcionesEapbs}
                    value={opcionesEapbs.find(o=>o.value===form.eps)||null} onChange={opt=>f("eps",opt?.value||"")} />
                </div>
                <div className="col-md-6">
                  <Form.Label>Régimen</Form.Label>
                  <div className="d-flex gap-3 mt-2">
                    {["Contributivo","Subsidiado"].map(op=><Form.Check key={op} type="radio" inline label={op} checked={form.regimen_eps===op} onChange={()=>f("regimen_eps",op)} />)}
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6"><Form.Label>Nivel educativo</Form.Label><Form.Control value={form.nivel_educativo} onChange={e=>f("nivel_educativo",e.target.value)} /></div>
                <div className="col-md-6"><Form.Label>Ocupación</Form.Label><Form.Control value={form.ocupacion} onChange={e=>f("ocupacion",e.target.value)} /></div>
              </div>

              <div className="border rounded p-3 bg-light" style={errores.ubicacion ? { borderColor:"#dc3545" } : {}}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">{req("📍 Georreferenciación")}</Form.Label>
                  <Button size="sm" variant="outline-primary" onClick={obtenerUbicacion} disabled={obteniendoGps}>
                    {obteniendoGps ? "Ubicando..." : "📍 Obtener ubicación actual"}
                  </Button>
                </div>
                {errorGps && <div className="text-danger mb-2" style={{fontSize:"0.85em"}}>{errorGps}</div>}
                {errores.ubicacion && <div className="text-danger mb-2" style={{fontSize:"0.85em"}}>{errores.ubicacion}</div>}
                <div className="row g-3">
                  <div className="col-md-4"><Form.Label style={{fontSize:"0.8rem"}}>Latitud</Form.Label><Form.Control value={form.latitud} isInvalid={!!errores.ubicacion} onChange={e=>f("latitud",e.target.value)} /></div>
                  <div className="col-md-4"><Form.Label style={{fontSize:"0.8rem"}}>Longitud</Form.Label><Form.Control value={form.longitud} isInvalid={!!errores.ubicacion} onChange={e=>f("longitud",e.target.value)} /></div>
                  <div className="col-md-4"><Form.Label style={{fontSize:"0.8rem"}}>Precisión (m)</Form.Label><Form.Control value={form.precision_gps} readOnly plaintext /></div>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h5 className="mb-3">2. Grupo poblacional y pertenencia diferencial</h5>
              <CheckGroup campo="grupo_poblacional" opciones={GRUPO_POBLACIONAL_OPCIONES} label={req("Grupo poblacional o condición de especial protección")} errorKey="grupo_poblacional" bloqueados={[ESTRATEGIA_ACTUAL]} />
              {esDiscapacidad && (
                <Form.Group className="mb-3">
                  <Form.Label>{req("Tipo de discapacidad")}</Form.Label>
                  <Form.Control value={form.tipo_discapacidad} isInvalid={!!errores.tipo_discapacidad} onChange={e=>f("tipo_discapacidad",e.target.value)} />
                </Form.Group>
              )}
              {form.grupo_poblacional.includes("Otro") && (
                <Form.Group className="mb-3"><Form.Label>¿Cuál otro?</Form.Label><Form.Control value={form.grupo_poblacional_otro} onChange={e=>f("grupo_poblacional_otro",e.target.value)} /></Form.Group>
              )}

              <SiNo campo="reconoce_grupo_diferencial" label={req("¿Se reconoce o pertenece a un grupo étnico, poblacional o diferencial?")} />
              {form.reconoce_grupo_diferencial === "Si" && (
                <>
                  <CheckGroup campo="pertenencia_diferencial" opciones={PERTENENCIA_OPCIONES} label={req("Pertenencia")} errorKey="pertenencia_diferencial" />
                  {form.pertenencia_diferencial.includes("Indígena") && (
                    <Form.Group className="mb-3">
                      <Form.Label>{req("Etnia")}</Form.Label>
                      <Select
                        classNamePrefix="rs"
                        styles={estiloSelectInvalido(!!errores.etnia)}
                        placeholder="Buscar etnia..."
                        options={opcionesEtnias}
                        value={opcionesEtnias.find(o => o.value === form.etnia) || null}
                        onChange={opt => f("etnia", opt?.value || "")}
                      />
                      {errores.etnia && <div className="text-danger mt-1" style={{fontSize:"0.875em"}}>{errores.etnia}</div>}
                    </Form.Group>
                  )}
                </>
              )}

              <SiNo campo="tiene_condicion_diferencial" label="¿Presenta alguna condición diferencial que pueda generar barreras para acceder a derechos o servicios?" />
              {form.tiene_condicion_diferencial === "Si" && (
                <Form.Group className="mb-3"><Form.Label>¿Cuál?</Form.Label><Form.Control value={form.condicion_diferencial_cual} onChange={e=>f("condicion_diferencial_cual",e.target.value)} /></Form.Group>
              )}
              <CheckGroup campo="barreras_identificadas" opciones={BARRERAS_OPCIONES} label="Barreras identificadas" />
            </>
          )}

          {step === 3 && esMigrante && (
            <>
              <h5 className="mb-3">3. Condición migratoria</h5>
              <p className="text-muted small">Aplica porque se marcó "Migrante" en el grupo poblacional.</p>
              <div className="row g-3 mb-3">
                <div className="col-md-6"><Form.Label>País de origen</Form.Label><Form.Control value={form.pais_origen} onChange={e=>f("pais_origen",e.target.value)} /></div>
                <div className="col-md-6">
                  <Form.Label>Situación migratoria</Form.Label>
                  <Form.Select value={form.situacion_migratoria} onChange={e=>f("situacion_migratoria",e.target.value)}>
                    <option value="">Seleccione</option><option>Regular</option><option>Irregular</option><option>PEP/PPT</option>
                  </Form.Select>
                </div>
              </div>
              <SiNo campo="documentos_vigentes" label="¿Documentos de identificación vigentes?" />
              <Form.Group className="mb-3">
                <Form.Label>Fecha de ingreso a Colombia</Form.Label>
                <Form.Control type="date" value={form.fecha_ingreso_colombia} onChange={e=>f("fecha_ingreso_colombia",e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3"><Form.Label>Motivo de migración</Form.Label><Form.Control value={form.motivo_migracion} onChange={e=>f("motivo_migracion",e.target.value)} /></Form.Group>
              <SiNo campo="red_apoyo_familiar" label="¿Tiene red de apoyo familiar?" />
              <SiNo campo="dificultad_salud_migratoria" label="¿Ha tenido dificultades para acceder a servicios de salud por su condición migratoria?" />
            </>
          )}

          {step === 4 && (
            <>
              <h5 className="mb-3">4. Condiciones de salud</h5>
              <SiNo campo="enfermedades_cronicas" label="¿Enfermedades crónicas?" />
              {form.enfermedades_cronicas === "Si" && (
                <Form.Group className="mb-3"><Form.Label>¿Cuál?</Form.Label><Form.Control value={form.enfermedades_cronicas_cual} onChange={e=>f("enfermedades_cronicas_cual",e.target.value)} /></Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Vacunación al día</Form.Label>
                <div className="d-flex gap-3">{["Si","No","No sabe"].map(op=><Form.Check key={op} type="radio" inline label={op} checked={form.vacunacion_al_dia===op} onChange={()=>f("vacunacion_al_dia",op)} />)}</div>
              </Form.Group>
              <SiNo campo="controles_medicos" label="¿Controles médicos al día?" />
              <Form.Group className="mb-3"><Form.Label>Observaciones de salud</Form.Label><Form.Control as="textarea" rows={2} value={form.observaciones_salud} onChange={e=>f("observaciones_salud",e.target.value)} /></Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Estado funcional</Form.Label>
                <div className="d-flex gap-3">{["Independiente","Dependiente"].map(op=><Form.Check key={op} type="radio" inline label={op} checked={form.estado_funcional===op} onChange={()=>f("estado_funcional",op)} />)}</div>
              </Form.Group>
              <SiNo campo="requiere_apoyo_tecnico" label="¿Requiere apoyo técnico?" />
              {form.requiere_apoyo_tecnico === "Si" && (
                <Form.Group className="mb-3"><Form.Label>¿Cuál?</Form.Label><Form.Control value={form.apoyo_tecnico_cual} onChange={e=>f("apoyo_tecnico_cual",e.target.value)} /></Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Estado de ánimo</Form.Label>
                <div className="d-flex flex-wrap gap-3">{["Neutro","Bajo/desanimado","Alto/eufórico","Otro"].map(op=><Form.Check key={op} type="radio" inline label={op} checked={form.estado_animo===op} onChange={()=>f("estado_animo",op)} />)}</div>
              </Form.Group>
              <SiNo campo="animo_interfiere_actividades" label="¿El estado de ánimo interfiere con sus actividades?" />
            </>
          )}

          {step === 5 && (
            <>
              <h5 className="mb-3">5. Determinantes sociales</h5>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de vivienda</Form.Label>
                <Form.Select value={form.tipo_vivienda} onChange={e=>f("tipo_vivienda",e.target.value)}>
                  <option value="">Seleccione</option>
                  <option>Casa</option><option>Apartamento</option><option>Habitación</option><option>Albergue</option><option>Vivienda de familiares</option><option>Otra</option>
                </Form.Select>
              </Form.Group>
              <CheckGroup campo="servicios_basicos" opciones={SERVICIOS_BASICOS_OPCIONES} label="Servicios básicos" />
              <Form.Group className="mb-3">
                <Form.Label>Ingresos</Form.Label>
                <Form.Select value={form.ingresos} onChange={e=>f("ingresos",e.target.value)}>
                  <option value="">Seleccione</option>
                  <option>Pensión</option><option>Trabajo</option><option>Subsidio</option><option>Ninguno</option><option>Otro</option>
                </Form.Select>
              </Form.Group>
              <SiNo campo="ingresos_suficientes" label="¿Los ingresos son suficientes para cubrir necesidades básicas?" />
              <CheckGroup campo="red_apoyo" opciones={RED_APOYO_OPCIONES} label="Red de apoyo" />
            </>
          )}

          {step === 6 && (
            <>
              <h5 className="mb-3">6. Identificación de violencias y vulneraciones de derechos</h5>
              <Form.Group className="mb-3">
                <Form.Label>¿Se ha sentido víctima o ha experimentado alguna situación de violencia o vulneración?</Form.Label>
                <div className="d-flex gap-3">{["Si","No","No sabe"].map(op=><Form.Check key={op} type="radio" inline label={op} checked={form.victima_violencia===op} onChange={()=>f("victima_violencia",op)} />)}</div>
              </Form.Group>
              <CheckGroup campo="violencias_identificadas" opciones={VIOLENCIAS_OPCIONES} label="Violencias identificadas" />
              <CheckGroup campo="otras_vulneraciones" opciones={OTRAS_VULNERACIONES_OPCIONES} label="Otras vulneraciones" />
              {form.otras_vulneraciones.includes("Otra") && (
                <Form.Group className="mb-3"><Form.Label>¿Cuál?</Form.Label><Form.Control value={form.otras_vulneraciones_cual} onChange={e=>f("otras_vulneraciones_cual",e.target.value)} /></Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>¿La situación se encuentra actualmente activa?</Form.Label>
                <div className="d-flex gap-3">{["Si","No","No sabe"].map(op=><Form.Check key={op} type="radio" inline label={op} checked={form.situacion_activa===op} onChange={()=>f("situacion_activa",op)} />)}</div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Presunto responsable o ámbito</Form.Label>
                <Form.Select value={form.presunto_responsable} onChange={e=>f("presunto_responsable",e.target.value)}>
                  <option value="">Seleccione</option>
                  <option>Familiar</option><option>Pareja</option><option>Comunitario</option><option>Institucional</option><option>Laboral</option><option>Espacio público</option><option>Otro</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3"><Form.Label>Descripción/observaciones</Form.Label><Form.Control as="textarea" rows={3} value={form.descripcion_observaciones} onChange={e=>f("descripcion_observaciones",e.target.value)} /></Form.Group>
            </>
          )}

          {step === 7 && (
            <>
              <h5 className="mb-3">7. Necesidades principales y ruta de atención</h5>
              <CheckGroup campo="necesidades_principales" opciones={NECESIDADES_OPCIONES} label="Seleccione las necesidades principales" />
              {form.necesidades_principales.includes("Otra") && (
                <Form.Group className="mb-3"><Form.Label>¿Cuál?</Form.Label><Form.Control value={form.necesidades_otra} onChange={e=>f("necesidades_otra",e.target.value)} /></Form.Group>
              )}
              <CheckGroup campo="requiere_activacion" opciones={RUTA_ATENCION_OPCIONES} label="¿Requiere activación por garantía de derecho?" />
              {form.requiere_activacion.includes("Otra") && (
                <Form.Group className="mb-3"><Form.Label>¿Cuál?</Form.Label><Form.Control value={form.requiere_activacion_otra} onChange={e=>f("requiere_activacion_otra",e.target.value)} /></Form.Group>
              )}
            </>
          )}

          {step === 8 && (
            <>
              <h5 className="mb-3">8. Firmas — funcionarios responsables</h5>
              {[1,2,3].map(n => (
                <div key={n} className="border rounded p-3 mb-3 bg-light">
                  <p className="mb-2 text-muted small">Funcionario {n} {n>1 && "(opcional)"}</p>
                  <div className="row g-2">
                    <div className="col-md-5"><Form.Label>Nombres y apellidos</Form.Label><Form.Control value={form[`funcionario${n}_nombre`]} onChange={e=>f(`funcionario${n}_nombre`,e.target.value)} /></div>
                    <div className="col-md-4"><Form.Label>Cargo</Form.Label><Form.Control value={form[`funcionario${n}_cargo`]} onChange={e=>f(`funcionario${n}_cargo`,e.target.value)} /></div>
                    <div className="col-md-3"><Form.Label>N° documento</Form.Label><Form.Control value={form[`funcionario${n}_documento`]} onChange={e=>f(`funcionario${n}_documento`,e.target.value)} /></div>
                  </div>
                </div>
              ))}
              <Form.Group className="mb-3"><Form.Label>Gestor / Técnico</Form.Label><Form.Control value={form.gestor} readOnly plaintext style={{background:"#f8f9fa",fontWeight:500}} /></Form.Group>
              <div className="alert alert-info py-2 px-3" style={{fontSize:"0.85rem"}}>
                ✍️ El consentimiento informado firmado se sube como PDF desde la tabla, una vez guardado este registro.
              </div>
            </>
          )}

        </Form>
      </Modal.Body>

      <Modal.Footer>
        {step > 1 && <Button variant="secondary" onClick={prevStep}>Anterior</Button>}
        {step < TOTAL_PASOS ? (
          <Button onClick={nextStep}>Siguiente</Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Guardando..." : persona ? "Actualizar" : "Guardar"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
